import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import styled from 'styled-components';
import { path } from 'ramda';
import { HelpInfosFilter, ServiceType, ServiceTypesGroupingModeValues, Situation } from 'types';
import { Radio } from 'antd';

import Input from 'components/organisms/input';
import Button from 'components/molecules/button';
import { useIntl } from 'common/config/i18n';
import { StyledBlueLink } from 'common/styling/mixins';
import FiltersCard from 'components/organisms/filters-card';
import InputSelect from 'components/organisms/select';

type FormValues = Omit<HelpInfosFilter, 'pageNumber' | 'maxResultPage'>;

interface Props {
  fetchListAction: (params?: HelpInfosFilter) => void;
  filterValues?: HelpInfosFilter;
  situationsLoading: boolean;
  situations: Situation[];
  serviceTypesLoading: boolean;
  serviceTypes: ServiceType[];
}

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
  margin-left: 10px;
`;

const ServiceTypesWrapper = styled.div`
  padding: 5px;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ServiceTypesRadio = styled.div`
  position: absolute;
  top: 55px;
  width: 350px;
`;

const Form = ({
  fetchListAction,
  filterValues,
  situationsLoading,
  situations,
  serviceTypesLoading,
  serviceTypes,
}: Props) => {
  const { t } = useIntl();

  useEffect(() => {
    if (filterValues) {
      formik.setFieldValue('SubFamily', filterValues?.SubFamily);
      formik.setFieldValue('Content', filterValues?.Content);
      formik.setFieldValue('SituationIDs', filterValues?.SituationIDs);
      formik.setFieldValue('selectedServiceTypes', filterValues?.selectedServiceTypes);
      formik.setFieldValue('serviceTypesGroupingMode', filterValues?.serviceTypesGroupingMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  const initialValues = {
    SubFamily: undefined,
    Content: undefined,
    SituationIDs: undefined,
    serviceTypesGroupingMode: ServiceTypesGroupingModeValues.OU,
    selectedServiceTypes: undefined,
  };

  const onSubmit = (values: FormValues) => {
    fetchListAction(values);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => onSubmit(values),
  });

  const onReset = () => {
    formik.setValues(initialValues);
    fetchListAction();
  };

  const inputSelectSituationsData = [
    {
      id: 'situations',
      list:
        situations?.map((situation) => ({
          id: situation.id,
          name: `${situation?.situationCategory?.name} -${situation?.name}`,
        })) || [],
    },
  ];

  const inputSelectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        serviceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  return (
    <form onSubmit={formik.handleSubmit}>
      <FiltersCard
        title={t('filtre', 'SEARCH_FILTER_TITLE')}
        firstPart={
          <>
            <div>
              <UpLabel>{t('filtre', 'SITUATION')}</UpLabel>
              <InputSelect
                mode="multiple"
                onChange={(value) => formik.setFieldValue('SituationIDs', value)}
                value={formik.values.SituationIDs}
                data={inputSelectSituationsData}
                style={{ minWidth: '250px', borderRadius: '5px' }}
                dropdownStyle={{ minWidth: '20%' }}
                popupMatchSelectWidth
                showSearch
              />
            </div>
            <Input
              width="250px"
              upLabel={t('filtre', 'SUB_FAMILY')}
              name="SubFamily"
              onChange={formik.handleChange}
              value={formik.values.SubFamily}
            />
            <Input
              width="150px"
              upLabel={t('filtre', 'CONTENT')}
              name="Content"
              onChange={formik.handleChange}
              value={formik.values.Content}
            />
            {serviceTypes.length > 1 && (
              <ServiceTypesWrapper>
                <UpLabel>{t('filtre', 'TYPE_CIRCULATION')}</UpLabel>

                <InputSelect
                  mode="multiple"
                  onChange={(ids) => formik.setFieldValue('selectedServiceTypes', ids)}
                  value={formik.values.selectedServiceTypes}
                  data={inputSelectServiceTypesData}
                  style={{ minWidth: '250px', borderRadius: '5px', marginRight: 10 }}
                  dropdownStyle={{ minWidth: '20%' }}
                  popupMatchSelectWidth
                  showSearch
                />
                <ServiceTypesRadio>
                  <Radio.Group
                    onChange={(e) => formik.setFieldValue('serviceTypesGroupingMode', e.target.value)}
                    value={formik.values.serviceTypesGroupingMode}
                  >
                    <Radio value={ServiceTypesGroupingModeValues.ET}>{t('common', 'and')}</Radio>
                    <Radio value={ServiceTypesGroupingModeValues.OU}>{t('common', 'or')}</Radio>
                  </Radio.Group>
                </ServiceTypesRadio>
              </ServiceTypesWrapper>
            )}
          </>
        }
        secondPart={
          <>
            <Button
              loading={situationsLoading || serviceTypesLoading}
              borderRadius="20px"
              htmlType="submit"
              width="130px"
            >
              {t('filtre', 'SEARCH_FILTER_FILTER_BUTTON')}
            </Button>
            <StyledBlueLink onClick={onReset} className="resetLink">
              {t('filtre', 'SEARCH_FILTER_RESET_BUTTON')}
            </StyledBlueLink>
          </>
        }
      />
    </form>
  );
};
export default Form;
